import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '../components/SuperAdminLayout';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import '../styles/superadmin.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const BASE = 'https://menu.searchisend.in';

const SuperadminAdAnalytics = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem('saas_superadmin_token');
    
    const [ads, setAds] = useState([]);
    const [selectedAdId, setSelectedAdId] = useState('all');
    const [days, setDays] = useState(30);
    const [analytics, setAnalytics] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!token) {
            navigate('/superadmin_v2_secure_login');
            return;
        }
        fetchAds();
        // eslint-disable-next-line
    }, [token, navigate]);

    useEffect(() => {
        if (token) {
            fetchAnalytics();
        }
        // eslint-disable-next-line
    }, [selectedAdId, days]);

    const fetchAds = async () => {
        try {
            const fd = new FormData();
            fd.append('action', 'fetch_ads');
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_ads.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                setAds(data.ads || []);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('action', 'get_ad_analytics');
            fd.append('ad_id', selectedAdId === 'all' ? 0 : selectedAdId);
            fd.append('days', days);
            
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_ads.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                setAnalytics(data.analytics || []);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Process data for Chart.js
    const processChartData = () => {
        // Generate an array of dates for the last N days
        const dates = [];
        for (let i = days - 1; i >= 0; i--) {
            const d = new Date();
            d.setDate(d.getDate() - i);
            dates.push(d.toISOString().split('T')[0]);
        }

        const impressions = new Array(days).fill(0);
        const clicks = new Array(days).fill(0);

        analytics.forEach(row => {
            const idx = dates.indexOf(row.date);
            if (idx !== -1) {
                if (row.event_type === 'Impression') impressions[idx] += parseInt(row.count, 10);
                if (row.event_type === 'Click') clicks[idx] += parseInt(row.count, 10);
            }
        });

        // Format dates nicely for x-axis
        const labels = dates.map(d => {
            const dateObj = new Date(d);
            return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        });

        return {
            labels,
            datasets: [
                {
                    label: 'Impressions',
                    data: impressions,
                    borderColor: 'rgba(99, 102, 241, 1)',
                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                    tension: 0.4,
                    fill: true,
                    yAxisID: 'y'
                },
                {
                    label: 'Clicks',
                    data: clicks,
                    borderColor: 'rgba(34, 197, 94, 1)',
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    tension: 0.4,
                    fill: true,
                    yAxisID: 'y1'
                }
            ]
        };
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    color: '#94a3b8',
                    font: { family: 'Inter', size: 12 }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                titleColor: '#fff',
                bodyColor: '#cbd5e1',
                padding: 12,
                cornerRadius: 8,
                titleFont: { size: 14, family: 'Inter' },
                bodyFont: { size: 13, family: 'Inter' }
            }
        },
        scales: {
            x: {
                grid: { color: 'rgba(255,255,255,0.05)' },
                ticks: { color: '#94a3b8' }
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                grid: { color: 'rgba(255,255,255,0.05)' },
                ticks: { color: '#94a3b8' }
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                grid: { drawOnChartArea: false },
                ticks: { color: '#94a3b8' }
            }
        }
    };

    return (
        <SuperAdminLayout>
            <div className="sa-container">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                    <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.8rem', display: 'flex', alignItems: 'center' }}>
                        <i className="fas fa-chart-line" style={{ color: 'var(--accent-primary)', marginRight: '15px' }}></i>
                        Ad Analytics
                    </h2>
                </div>

                <div style={{ display: 'flex', gap: '20px', marginBottom: '25px', background: 'var(--bg-card)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                    <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Filter by Ad Campaign</label>
                        <select 
                            className="sa-select"
                            value={selectedAdId}
                            onChange={(e) => setSelectedAdId(e.target.value)}
                        >
                            <option value="all">All Ads Platform-Wide</option>
                            {ads.map(ad => (
                                <option key={ad.id} value={ad.id}>{ad.title} (Sponsor #{ad.sponsor_id})</option>
                            ))}
                        </select>
                    </div>
                    <div style={{ width: '200px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Time Range</label>
                        <select 
                            className="sa-select"
                            value={days}
                            onChange={(e) => setDays(parseInt(e.target.value))}
                        >
                            <option value={7}>Last 7 Days</option>
                            <option value={30}>Last 30 Days</option>
                            <option value={90}>Last 90 Days</option>
                        </select>
                    </div>
                </div>

                <div style={{ background: 'var(--bg-card)', padding: '25px', borderRadius: '12px', border: '1px solid var(--border-color)', minHeight: '400px' }}>
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '350px', color: 'var(--text-secondary)' }}>
                            <i className="fas fa-spinner fa-spin fa-2x"></i>
                        </div>
                    ) : (
                        <div style={{ height: '350px' }}>
                            <Line data={processChartData()} options={chartOptions} />
                        </div>
                    )}
                </div>
            </div>
        </SuperAdminLayout>
    );
};

export default SuperadminAdAnalytics;
